import { useCallback, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { isEmpty } from '@rainbow-modules/validation';
import CreateEditOpportunityStageForm from 'components/CreateEditOpportunityStageForm';
import ConfigOpportunityStageTrackingForm from 'components/ConfigOpportunityStageTrackingForm';
import KanbanBoard from 'components/KanbanBoard';
import { BoardCard, BoardColumn, CardMoveEvent } from 'components/KanbanBoard/types';
import { ViewContextType } from 'pages/Opportunities/types';
import { OPPORTUNITY_EVENTS } from 'analytics/posthog/types';
import useTrackEvent from 'hooks/useTrackEvent';
import useManageOpportunities from '../../../../data/hooks/useManageOpportunities';
import { BoardContainer } from './styled';
import StageSummary from './summary';
import StageInfo from './stageInfo';
import EmptySearchMessage from './emptySearchMessage';
import useBoardStorage from './hooks/useBoardStorage';
import OpportunityCard from './OpportunityCard/index';
import useBoardOpportunities from './hooks/useBoardOpportunities';

const OpportunitiesViewBoard = () => {
    const { agentId = '' } = useParams();
    const { track } = useTrackEvent();
    const {
        isLoading: isLoadingOpportunities, opportunities, totals,
    } = useBoardOpportunities(agentId);

    const {
        selectedPipeline = '',
        stages,
        isLoading: isLoadingPipelineData,
        searchQuery,
    } = useOutletContext<ViewContextType>();

    const isLoading = isLoadingPipelineData || isLoadingOpportunities;

    const {
        collapsedColumns,
        expandColumn,
        collapseColumn,
    } = useBoardStorage({ pipelineId: selectedPipeline });

    const {
        create: createNewOpportunity,
        updateField: updateOpportunityField,
    } = useManageOpportunities({ agentId });

    const boardColumns = useMemo(
        () => stages?.map((stage, index) => ({
            name: stage.id,
            label: stage.name,
            removable: stage.removable && opportunities[stage.id]?.length === 0,
            draggable: stage.removable,
            editable: true,
            collapsed: collapsedColumns.includes(stage.name),
            position: index,
            data: {
                ...stage,
                totalAmount: totals[stage.id]?.totalAmount || 0,
                totalOpportunities: totals[stage.id]?.numberOfOpportunities || 0,
            },
        })),
        [collapsedColumns, opportunities, stages, totals],
    );

    const isEmptyBoard = !isLoading && Object.values(opportunities).every(
        (columnCards) => columnCards.length === 0,
    );

    const handleAddNewOpportunity = useCallback(
        (columnName: string) => {
            track(OPPORTUNITY_EVENTS.BOARD_OPPORTUNITY_ADDED, {
                pipelineId: selectedPipeline,
                stage: columnName,
            });
            createNewOpportunity({
                pipeline: selectedPipeline,
                stageId: columnName,
            });
        },
        [createNewOpportunity, selectedPipeline, track],
    );

    const handleChangeOpportunityStage = useCallback(
        async ({ cardId, destinationColumn, sourceColumn }: CardMoveEvent) => {
            try {
                await updateOpportunityField(cardId as string, 'stageId')(destinationColumn);
                track(OPPORTUNITY_EVENTS.BOARD_OPPORTUNITY_MOVED, {
                    opportunityId: cardId,
                    fromStage: sourceColumn,
                    toStage: destinationColumn,
                });
                return true;
            } catch (error) {
                // no catch
            }
            return false;
        },
        [updateOpportunityField, track],
    );

    const handleToggleColumn = useCallback(
        (column: BoardColumn) => {
            const action = collapsedColumns.includes(column.label) ? 'expand' : 'collapse';
            track(OPPORTUNITY_EVENTS.BOARD_COLUMN_TOGGLED, { column: column.label, action });
            (collapsedColumns.includes(column.label) ? expandColumn : collapseColumn)(column.label);
        },
        [collapseColumn, expandColumn, collapsedColumns, track],
    );

    return (
        <BoardContainer>
            <RenderIf isTrue={isEmpty(searchQuery) || !isEmptyBoard}>
                <KanbanBoard
                    isLoading={Object.values(isLoading).some((value) => value)}
                    columns={boardColumns}
                    cards={opportunities as unknown as Record<string, BoardCard[]>}
                    cardComponent={OpportunityCard}
                    columnHeaderComponent={StageInfo}
                    columnFooterComponent={StageSummary}
                    onAddCardClick={handleAddNewOpportunity}
                    onCardMoved={handleChangeOpportunityStage}
                    onColumnCollapse={handleToggleColumn}
                    onColumnExpand={handleToggleColumn}
                />
            </RenderIf>
            <RenderIf isTrue={!isEmpty(searchQuery) && isEmptyBoard}>
                <EmptySearchMessage query={searchQuery} />
            </RenderIf>
            <CreateEditOpportunityStageForm />
            <ConfigOpportunityStageTrackingForm />
        </BoardContainer>
    );
};

export default OpportunitiesViewBoard;
